// src/components/MyCompetitionsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuLayout from "./MenuLayout";
import "../styles/MyCompetitionsPage.css";
import { getCompetitionTypes, getMyRank, getTop10Entries } from "../api/competition";

const competitionsTabs = [
  { id: "weekly", label: "مسابقه هفتگی" },
  { id: "monthly", label: "مسابقه ماهانه" },
];

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("fa-IR");
};

const formatMoney = (val) => {
  const num = Number(val || 0);
  if (Number.isNaN(num)) return "-";
  return `$${num.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};

const rankMedal = (rank) => {
  if (rank === 1) return "/images/competitions/medal-gold.svg";
  if (rank === 2) return "/images/competitions/medal-silver.svg";
  if (rank === 3) return "/images/competitions/medal-bronze.svg";
  return null;
};

const MyCompetitionsPage = () => {
  const [activeCompetition, setActiveCompetition] = useState("weekly");
  const [competition, setCompetition] = useState(null);
  const [entries, setEntries] = useState([]);
  const [rank, setRank] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = async (showLoading = false) => {
      if (showLoading) setLoading(true);
      try {
        const res = await getCompetitionTypes();
        if (cancelled) return;
        const comps = Array.isArray(res?.competitions) ? res.competitions : [];
        const type = activeCompetition === "weekly" ? "weekly" : "monthly";
        const selected = comps.find((c) => c.type === type) || comps[0] || null;
        setCompetition(selected);
        if (selected?.id) {
          const [topRes, rankRes] = await Promise.allSettled([
            getTop10Entries(selected.id),
            getMyRank(selected.id),
          ]);
          if (cancelled) return;
          if (topRes.status === "fulfilled") setEntries(Array.isArray(topRes.value) ? topRes.value : []);
          else setEntries([]);
          if (rankRes.status === "fulfilled") setRank(rankRes.value?.rank ?? null);
          else setRank(null);
        } else {
          setEntries([]);
          setRank(null);
        }
      } catch {
        setEntries([]);
        setRank(null);
        setCompetition(null);
      } finally {
        if (!cancelled && showLoading) setLoading(false);
      }
    };
    load(true);
    const refreshTimer = window.setInterval(() => load(false), 15000);
    return () => {
      cancelled = true;
      window.clearInterval(refreshTimer);
    };
  }, [activeCompetition]);

  const rows = useMemo(
    () =>
      entries.map((entry, idx) => ({
        id: idx + 1,
        rank: idx + 1,
        name: entry.full_name || entry.user_email || "-",
        date: formatDate(entry.join_time),
        payout: formatMoney(entry.profit),
        account_number: entry.analysis_account_number || "",
      })),
    [entries]
  );
  const defaultAccountNumber = rows[0]?.account_number;

  return (
    <MenuLayout>
      <div className="mycomp">
        <header className="mycomp__header">
          <div className="mycomp__title">مسابقات جاری من</div>
          <p className="mycomp__subtitle">ببین کجای کاری نیاز به بیشتر تلاش کنی یا بقیه باید برسن بهت!</p>
        </header>

        <div className="mycomp__tabs">
          {competitionsTabs.map((tab) => (
            <button
              key={tab.id}
              className={`mycomp__tab ${activeCompetition === tab.id ? "is-active" : ""}`}
              onClick={() => setActiveCompetition(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mycomp__empty">
            <div className="mycomp__empty-card">
              <div className="mycomp__empty-title">در حال دریافت اطلاعات...</div>
            </div>
          </div>
        ) : competition ? (
          <>
            <div className="mycomp__rank-card">
              <div className="mycomp__rank-line">
                <div className="mycomp__rank-label">
                  رتبه شما در مسابقات جاری
                  <img src="/images/competitions/rank-icon.svg" alt="رتبه" />
                </div>
                <div className="mycomp__rank-value">{rank ?? "-"}</div>
              </div>
            </div>

            <div className="mycomp__table">
              <div className="mycomp__table-head">
                <span>رتبه</span>
                <span>نام شرکت کننده</span>
                <span>تاریخ</span>
                <span>سود تجمیعی</span>
                <span></span>
              </div>
              <div className="mycomp__table-body">
                {rows.length === 0 ? (
                  <div className="mycomp__row">اطلاعاتی برای نمایش وجود ندارد.</div>
                ) : (
                  rows.map((row) => {
                    const medal = rankMedal(row.rank);
                    return (
                      <div key={row.id} className="mycomp__row">
                        <span className="mycomp__cell-rank">
                          {medal ? <img src={medal} alt={`رتبه ${row.rank}`} /> : <span className="mycomp__rank-number">{row.rank}</span>}
                        </span>
                        <span className="mycomp__cell-user">{row.name}</span>
                        <span className="mycomp__cell-date">{row.date}</span>
                        <span className="mycomp__cell-payout">{row.payout}</span>
                        <span className="mycomp__cell-action">
                          {row.account_number && (
                            <button
                              className="mycomp__details-btn"
                              onClick={() => navigate(`/analysis/${encodeURIComponent(row.account_number)}`)}
                            >
                              تحلیل چالش
                            </button>
                          )}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            <div className="mycomp__account-btn-wrap">
              <button
                className="mycomp__account-btn"
                onClick={() =>
                  navigate(
                    defaultAccountNumber
                      ? `/analysis/${encodeURIComponent(defaultAccountNumber)}`
                      : "/challenges"
                  )
                }
              >
                تحلیل حساب شرکت‌کننده
              </button>
            </div>
          </>
        ) : (
          <div className="mycomp__empty">
            <div className="mycomp__empty-card">
              <div className="mycomp__empty-title">مسابقه فعالی برای نمایش وجود ندارد</div>
              <div className="mycomp__empty-sub">
                بعد از فعال شدن مسابقه، اطلاعات شما در این بخش نمایش داده می‌شود.
              </div>
            </div>
          </div>
        )}
      </div>
    </MenuLayout>
  );
};

export default MyCompetitionsPage;
